import { useEffect } from "react";
import { RotateCcw } from "lucide-react";
import { useFaceCapture, type HintTone } from "./useFaceCapture";

interface FaceCaptureViewProps {
  onCaptured: (image: string) => void;
  requireBlink?: boolean;
}

const TONE_CLASSES: Record<HintTone, string> = {
  neutral: "text-cn-text",
  info: "text-cn-primary",
  warn: "text-amber-600",
  success: "text-green-600",
};

const OVAL_RING: Record<string, string> = {
  loading: "ring-gray-300",
  prep: "ring-gray-300",
  position: "ring-cn-primary/60",
  blink: "ring-cn-primary",
  hold: "ring-cn-primary",
  done: "ring-green-500",
  error: "ring-amber-500",
};

/**
 * Камера с овалом и подсказкой. Включается монтированием — родитель рисует
 * компонент ровно тогда, когда камера нужна, и снимает, когда нет.
 *
 * Превью зеркальное, как селфи: иначе «сдвиньтесь влево» ведёт вправо. Снимок
 * при этом НЕ зеркальный — сверяется лицо как есть.
 */
export function FaceCaptureView({ onCaptured, requireBlink }: FaceCaptureViewProps) {
  const { videoRef, state, start } = useFaceCapture({ onCaptured, requireBlink });

  useEffect(() => {
    void start();
  }, [start]);

  const showVideo = state.phase !== "error";

  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className={`relative w-[min(72vw,300px)] aspect-[4/5] rounded-[50%] overflow-hidden bg-black ring-4 transition-colors ${
          OVAL_RING[state.phase] ?? "ring-gray-300"
        }`}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`absolute inset-0 h-full w-full object-cover -scale-x-100 ${
            showVideo ? "" : "hidden"
          }`}
        />
        {state.phase === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 text-white">
            <div className="h-8 w-8 rounded-full border-2 border-white border-t-transparent animate-spin" />
            {state.loadProgress > 0 && (
              <span className="text-xs tabular-nums">{state.loadProgress}%</span>
            )}
          </div>
        )}
        {state.phase === "hold" && (
          <div className="absolute inset-x-6 bottom-6 h-1.5 rounded-full bg-white/30">
            <div
              className="h-full rounded-full bg-white transition-[width] duration-100"
              style={{ width: `${state.holdPercent}%` }}
            />
          </div>
        )}
      </div>

      {state.phase === "error" ? (
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-amber-700" role="alert">
            {state.error}
          </p>
          <button
            type="button"
            onClick={() => void start()}
            className="inline-flex items-center gap-1.5 rounded-cn-lg border border-cn-border px-4 py-2 text-sm font-medium text-cn-text hover:bg-gray-50"
          >
            <RotateCcw className="h-4 w-4" />
            Повторить
          </button>
        </div>
      ) : (
        <p
          className={`min-h-[2.5rem] text-center text-sm font-medium leading-snug ${TONE_CLASSES[state.tone]}`}
          role="status"
          aria-live="polite"
        >
          {state.hint}
        </p>
      )}
    </div>
  );
}
